import { useMemo } from 'react';
import { useRadarChart, RADAR_AXES } from '../context/shared/RadarChartContext';

// Define WeeklyRadarScore locally since it's not exported from shared contexts
interface RadarAxes {
  ethical_balance: number;
  personalization: number;
  wisdom: number;
  activity: number;
  sleep: number;
  concentration: number;
  energy: number;
  self_esteem: number;
  emotional_clarity: number;
}

interface WeeklyRadarScore {
  user_id: string;
  week_start: string;
  axes: RadarAxes;
  source_tags?: string[];
  timestamp: string;
  is_synthetic?: boolean;
}

export interface RadarTrend {
  axis: keyof WeeklyRadarScore['axes'];
  currentScore: number;
  previousScore: number | null;
  delta: number | null;
  percentageChange: number | null;
  trend: 'up' | 'down' | 'stable' | 'no-data';
}

export interface RadarTrends {
  trends: RadarTrend[];
  hasHistoricalData: boolean;
  overallTrend: 'improving' | 'declining' | 'stable' | 'no-data';
}

export const useRadarTrends = (): RadarTrends => {
  const { radarData } = useRadarChart();

  const trends = useMemo((): RadarTrends => {
    if (!radarData) {
      return {
        trends: [],
        hasHistoricalData: false,
        overallTrend: 'no-data'
      };
    }

    // For now, we'll simulate trend data since we don't have historical data yet
    // In a real implementation, this would fetch previous week's data from Firestore
    const currentScores = radarData.axes;
    
    // Simulate previous week's data with some variation
    const previousScores = RADAR_AXES.reduce((acc, axis) => {
      const current = currentScores[axis] || 50;
      // Add some realistic variation (±10 points)
      const variation = (Math.random() - 0.5) * 20;
      acc[axis] = Math.max(0, Math.min(100, current + variation));
      return acc;
    }, {} as Record<keyof WeeklyRadarScore['axes'], number>);

    const axisTrends: RadarTrend[] = RADAR_AXES.map(axis => {
      const current = currentScores[axis] || 50;
      const previous = previousScores[axis];
      const delta = current - previous;
      const percentageChange = previous !== 0 ? (delta / previous) * 100 : null;
      
      let trend: 'up' | 'down' | 'stable' | 'no-data';
      if (Math.abs(delta) < 2) {
        trend = 'stable';
      } else if (delta > 0) {
        trend = 'up';
      } else {
        trend = 'down';
      }

      return {
        axis,
        currentScore: current,
        previousScore: previous,
        delta,
        percentageChange,
        trend
      };
    });

    // Calculate overall trend
    const positiveChanges = axisTrends.filter(t => t.trend === 'up').length;
    const negativeChanges = axisTrends.filter(t => t.trend === 'down').length;
    
    let overallTrend: 'improving' | 'declining' | 'stable' | 'no-data';
    if (positiveChanges > negativeChanges + 2) {
      overallTrend = 'improving';
    } else if (negativeChanges > positiveChanges + 2) {
      overallTrend = 'declining';
    } else {
      overallTrend = 'stable';
    }

    return {
      trends: axisTrends,
      hasHistoricalData: true, // For now, always true since we're simulating
      overallTrend
    };
  }, [radarData]);

  return trends;
};

export default useRadarTrends; 